import React, { useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import {
  Fab,
  Tooltip,
  Zoom,
  useTheme,
  useMediaQuery,
  Box,
  Container,
  Typography,
  Skeleton,
  Divider,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import {
  GET_COURSE_TEACHINGS,
  GET_USER_DATA,
} from "../../apollo-client/queries";
import { TeacherSmallComponent } from "./teacher-small-component";
import { useFetchMoreOnScroll } from "../../helpers";
import { BecomeTeacherDialog } from "./dialogs/become-teacher-dialog";
import { showErrorToast } from "../../toast/toast-service";
import { StartTutoringMotivation } from "./start-tutoring-motivation";
import { ProfilePageVacationDeactivationDialog } from "../profile-page/profile-page-teachings/ProfilePageVacationDeactivationDialog";
import { useOpenClose } from "../../hooks/use-open-close";
import { usePleaseLoginInfo } from "../../hooks/use-please-login-info";
import { theme } from "../../theme";
import { TEACHING_SCOPES } from "../../constants2";
import { ContentContainer } from "../../components/layout/content-container";
import { MainLayout } from "../../components/layout/main-layout";

export const TeachersPage = (props) => {
  const [isBecomeTeacherPopupOpen, setIsBecomeTeacherPopupOpen] =
    useState(false);

  const match = useRouteMatch();
  const muiTheme = useTheme();
  // Use MUI breakpoint system instead of custom media queries
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const {
    data: teachingsData,
    error,
    loading: loadingTeachings,
    fetchMore,
  } = useQuery(GET_COURSE_TEACHINGS, {
    variables: {
      course: match.params.id,
      limit: 15,
      isActive: true,
      onHoliday: false,
    },
    pollInterval: 60000 * 5, // every 60 seconds x 5
    fetchPolicy: "cache-and-network",
  });

  const { data: userData } = useQuery(GET_USER_DATA, {
    variables: {
      courseId: match.params.id,
    },
  });

  const {
    value: isDeactivateVacationDialogOpen,
    toggle: toggleVacDeactivationDialog,
  } = useOpenClose(false);

  const { openPleaseLoginInfo } = usePleaseLoginInfo();

  let course = teachingsData?.courses?.courses?.[0] ?? [];
  const teachings = teachingsData?.courses?.courses?.[0]?.teachings?.teachings;
  const teachingsCount =
    teachingsData?.courses?.courses?.[0]?.teachings?.teachingsCount;

  useFetchMoreOnScroll(async () => {
    if (teachings?.length < teachingsCount) {
      await fetchMore({
        variables: {
          skip: teachings.length,
        },
      });
    }
  });

  const areTeachingsForModuleAvailable = () => {
    return Boolean(teachings?.[0]?.scope === TEACHING_SCOPES.IN_MODULE);
  };

  const handleBecomeTeacherClick = async (e, userData) => {
    if (!userData) {
      openPleaseLoginInfo();
      return;
    }

    if (userData && isAlreadyTeaching(teachings, userData)) {
      showAlreadyTeacherError();
      return;
    }
    if (userData && userData.user.onHoliday) {
      toggleVacDeactivationDialog();
    } else {
      setIsBecomeTeacherPopupOpen(true);
    }
  };

  const showAlreadyTeacherError = () => {
    showErrorToast(
      <FormattedMessage id={"add-teacher-button.already-tutor-error"} />
    );
  };

  const isAlreadyTeaching = (teachings, userData) => {
    return Boolean(
      userData &&
        teachings?.find(
          (teaching) =>
            teaching.teacher._id === userData.user._id &&
            teaching.scope === "IN_MODULE"
        )
    );
  };

  const shouldShowMotivationOnTop = () => {
    const areNoTeachingsAvailable = Boolean(
      teachings.length === 0 ||
        teachings?.[0]?.scope !== TEACHING_SCOPES.IN_MODULE
    );
    return areNoTeachingsAvailable;
  };

  const shouldShowOtherTeachingsText = (teaching, i, arr) => {
    return (
      (i === 0 && teaching.scope !== "IN_MODULE") ||
      (i !== 0 &&
        arr[i - 1].scope === "IN_MODULE" &&
        arr[i].scope !== "IN_MODULE")
    );
  };

  const shouldShowCourseTitle = (teaching, i, arr) => {
    return (
      (i === 0 && teaching.scope !== "IN_MODULE") ||
      (i !== 0 && arr[i - 1].course.title !== arr[i].course.title)
    );
  };

  return (
    <MainLayout
      title={course.title}
      university={course.university}
      subscriberCount={course.teachings ? course.teachings.subscriberCount : ""}
      loading={loadingTeachings}
      error={error}
      props={props}
    >
      <ContentContainer>
        <Container maxWidth="lg" sx={{ py: 3, pt: { xs: 6, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minHeight: "100%",
            }}
          >
            {!error && teachings && shouldShowMotivationOnTop() && (
              <Box
                sx={{
                  width: { xs: "90%", md: "100%" },
                  alignSelf: "center",
                  mt: { xs: 2, md: 0 }, // Add top margin on mobile for extra safety
                }}
              >
                <StartTutoringMotivation
                  subscriberCount={
                    course.teachings ? course.teachings.subscriberCount : ""
                  }
                  areTeachingsAvailable={areTeachingsForModuleAvailable()}
                />
              </Box>
            )}

            {!error &&
              teachings?.map((teaching, i, arr) => {
                return (
                  <Box
                    key={"LinkWrapper_" + i}
                    sx={{ width: { xs: "100%", md: "75%" }, mx: "auto" }}
                  >
                    {shouldShowOtherTeachingsText(teaching, i, arr) && (
                      <Typography
                        variant="h3"
                        sx={{
                          color: theme.mainColor,
                          fontWeight: "bold",
                          pt: 4,
                          borderTop: "1px solid #acacac",
                          textAlign: "left",
                          mb: 2,
                        }}
                      >
                        <FormattedMessage id={"teacher.further-teachings"} />
                      </Typography>
                    )}
                    {shouldShowCourseTitle(teaching, i, arr) && (
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: "bold",
                          mt: 6,
                          mb: 4,
                          textAlign: "left",
                        }}
                      >
                        {teaching.course.title}
                      </Typography>
                    )}
                    <Box
                      component={Link}
                      to={`/courses/${teachingsData.courses.courses[0]._id}/teachers/${teaching._id}`}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "block",
                        width: "100%",
                        "&:hover": {
                          textDecoration: "none",
                        },
                      }}
                    >
                      <TeacherSmallComponent
                        key={"TeacherComponent_" + i}
                        teaching={teaching}
                        showSmallTeacherComponent={true}
                        courseTitle={teachingsData.courses.courses[0].title}
                        hideLine={i === arr.length - 1}
                      />
                    </Box>
                    {i !== arr.length - 1 && (
                      <Divider
                        sx={{
                          display: { xs: "block", md: "none" },
                          borderColor: "#acacac",
                          my: 2,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            {loadingTeachings && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
              </Box>
            )}
          </Box>
        </Container>
        <ProfilePageVacationDeactivationDialog
          isDialogOpen={isDeactivateVacationDialogOpen}
          toggleDialog={toggleVacDeactivationDialog}
        />
      </ContentContainer>

      {teachings && (
        <Tooltip
          title={<FormattedMessage id={"teacher.add-new-teacher"} />}
          placement="left"
          arrow
        >
          <Zoom in={true} timeout={300}>
            <Fab
              data-testid="add-teacher-fab-button"
              disabled={isAlreadyTeaching(teachings, userData)}
              onClick={(e) =>
                handleBecomeTeacherClick(e, userData, props.match.params.id)
              }
              color="primary"
              sx={{
                position: "fixed",
                bottom: { xs: "70px", md: "30px" },
                right: { xs: "2%", md: "10%", lg: "15%", xl: "10%" },
                width: { xs: 56, md: 64 },
                height: { xs: 56, md: 64 },
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
                },
                "&:disabled": {
                  backgroundColor: muiTheme.palette.grey[300],
                  color: muiTheme.palette.grey[600],
                },
                "&:focus": {
                  outline: `2px solid ${muiTheme.palette.primary.main}`,
                  outlineOffset: "2px",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                zIndex: 1000, // Lower than chat components (1001-1002)
              }}
              aria-label="Add yourself as a teacher"
              aria-describedby="add-teacher-tooltip"
            >
              <AddIcon />
            </Fab>
          </Zoom>
        </Tooltip>
      )}

      {isBecomeTeacherPopupOpen && (
        <BecomeTeacherDialog
          closeDialog={() => setIsBecomeTeacherPopupOpen(false)}
          isDialogOpen={isBecomeTeacherPopupOpen}
          user={userData && userData.user}
          courseId={props.match.params.id}
          inactiveTeaching={userData.user.inactiveTeachings.teachings[0]}
        />
      )}
    </MainLayout>
  );
};
